const SETUP_STATES = new Set([
  'existing_required',
  'existing_repair_required',
  'creation_available',
  'recovery_available',
  'page_assignment_required',
  'blocked',
]);

const SAFE_CODE = /^[a-z][a-z0-9_]{0,119}$/;
const SAFE_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function text(value) {
  return typeof value === 'string' ? value.trim() : '';
}

export function isSafeMetaUuid(value) {
  return SAFE_UUID.test(text(value));
}

function safeCode(value, fallback) {
  const normalized = text(value).toLowerCase();
  return SAFE_CODE.test(normalized) ? normalized : fallback;
}

function uniqueUuids(value) {
  return Object.freeze([...new Set(
    (Array.isArray(value) ? value : []).map(text).filter((item) => SAFE_UUID.test(item)),
  )]);
}

function normalizeBinding(value, fields) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const binding = Object.fromEntries(fields.map((field) => [field, text(value[field])]));
  return Object.values(binding).every((item) => SAFE_UUID.test(item)) ? Object.freeze(binding) : null;
}

function uniqueBindings(value, fields) {
  const bindings = [];
  const seen = new Set();
  for (const item of Array.isArray(value) ? value : []) {
    const binding = normalizeBinding(item, fields);
    if (!binding) continue;
    const key = fields.map((field) => binding[field]).join(':');
    if (seen.has(key)) continue;
    seen.add(key);
    bindings.push(binding);
  }
  return Object.freeze(bindings);
}

function normalizeAssignmentCandidates(value) {
  const candidates = [];
  const seen = new Set();
  for (const item of Array.isArray(value) ? value : []) {
    const binding = normalizeBinding(item, ['pageResourceId', 'credentialId']);
    if (!binding) continue;
    const key = `${binding.credentialId}:${binding.pageResourceId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    candidates.push(Object.freeze({
      ...binding,
      verificationRequired: item.verificationRequired === true,
    }));
  }
  return Object.freeze(candidates);
}

export function normalizeInstantFormSetup(value) {
  const input = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const requestedState = text(input.state).toLowerCase();
  const state = SETUP_STATES.has(requestedState) ? requestedState : 'blocked';
  const creationPages = state === 'creation_available'
    ? uniqueBindings(input.creationPages, ['pageResourceId', 'credentialId'])
    : Object.freeze([]);
  const recovery = state === 'recovery_available'
    ? normalizeBinding(input.recovery, ['operationId', 'pageResourceId', 'credentialId'])
    : null;

  // An incomplete recovery binding cannot be widened into normal Page choice.
  // Collapse it to blocked while retaining only tenant-safe identifiers.
  const effectiveState = state === 'recovery_available' && !recovery ? 'blocked' : state;
  return Object.freeze({
    state: effectiveState,
    reasonCode: safeCode(
      input.reasonCode,
      effectiveState === 'blocked' ? 'instant_form_setup_unavailable' : effectiveState,
    ),
    formResourceIds: uniqueUuids(input.formResourceIds),
    compatibleCredentialIds: uniqueUuids(input.compatibleCredentialIds),
    creationPages: effectiveState === 'creation_available' ? creationPages : Object.freeze([]),
    recovery: effectiveState === 'recovery_available' ? recovery : null,
    assignments: uniqueBindings(input.assignments, ['assignmentId', 'pageResourceId', 'credentialId']),
    assignmentCandidates: normalizeAssignmentCandidates(input.assignmentCandidates),
    canManage: input.canManage === true,
    managerActionRequired: input.managerActionRequired === true,
  });
}

export function instantFormSetupUsesExistingForm(setup) {
  return ['existing_required', 'existing_repair_required'].includes(setup?.state);
}

export function instantFormSetupOffersCreation(setup) {
  return ['creation_available', 'recovery_available'].includes(setup?.state);
}

export function metaPageRowsForBindings(connections, bindings) {
  const safeConnections = Array.isArray(connections) ? connections : [];
  const pageMatches = new Map();
  for (const connection of safeConnections) {
    const credentialId = text(connection?.credentialId);
    if (!credentialId) continue;
    for (const page of Array.isArray(connection?.pages) ? connection.pages : []) {
      const pageResourceId = text(page?.resourceId);
      if (!pageResourceId) continue;
      const key = `${credentialId}:${pageResourceId}`;
      const existing = pageMatches.get(key) || [];
      existing.push(Object.freeze({
        credentialId,
        pageResourceId,
        label: text(page?.label) || 'Facebook Page',
        providerId: text(page?.providerId),
      }));
      pageMatches.set(key, existing);
    }
  }

  const rows = [];
  for (const binding of Array.isArray(bindings) ? bindings : []) {
    const credentialId = text(binding?.credentialId);
    const pageResourceId = text(binding?.pageResourceId);
    const matches = pageMatches.get(`${credentialId}:${pageResourceId}`) || [];
    if (matches.length !== 1) continue;
    rows.push(Object.freeze({ ...binding, ...matches[0] }));
  }
  return Object.freeze(rows);
}
